import React, {useCallback} from 'react'
import {useIntl} from 'react-intl'

import {SelectOption} from './SelectWithSearch'

export type AttendanceMultiSelectResult = {
  values: string[]
  labels: string[]
  selectAll: boolean
}

type AttendanceSchoolMultiSelectProps = {
  options: SelectOption[]
  onConfirm: (result: AttendanceMultiSelectResult) => void
  theme?: 'light' | 'dark'
  disabled?: boolean
  allowSelectAll?: boolean
}

const AttendanceSchoolMultiSelect: React.FC<AttendanceSchoolMultiSelectProps> = ({
  options,
  onConfirm,
  theme = 'light',
  disabled = false,
  allowSelectAll = true,
}) => {
  const intl = useIntl()

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'
  const textColor = theme === 'dark' ? '#ffffff' : '#212529'
  const mutedColor = theme === 'dark' ? 'rgba(255,255,255,0.75)' : '#6c757d'

  const handleSelectOption = useCallback(
    (option: SelectOption) => {
      if (disabled) return
      onConfirm({
        values: [option.value],
        labels: [option.label],
        selectAll: false,
      })
    },
    [disabled, onConfirm]
  )

  const handleSelectAll = useCallback(() => {
    if (disabled || options.length === 0) return
    onConfirm({
      values: options.map((o) => o.value),
      labels: options.map((o) => o.label),
      selectAll: true,
    })
  }, [disabled, onConfirm, options])

  return (
    <div className='attendance-school-multi-select' style={{marginBottom: 12}}>
      {allowSelectAll && options.length > 1 ? (
        <div className='d-flex justify-content-end mb-2'>
          <button
            type='button'
            className='btn btn-sm btn-outline-primary'
            disabled={disabled}
            onClick={handleSelectAll}
          >
            {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.SELECT_ALL'})}
          </button>
        </div>
      ) : null}
      <div
        className='rounded-3 p-2'
        style={{
          border: `1px solid ${borderColor}`,
          maxHeight: 220,
          overflowY: 'auto',
        }}
      >
        {options.map((option) => (
          <button
            key={option.value}
            type='button'
            disabled={disabled}
            onClick={() => handleSelectOption(option)}
            className='btn w-100 text-start mb-2'
            style={{
              backgroundColor: 'transparent',
              border: `1px solid ${borderColor}`,
              color: textColor,
              padding: '0.5rem 0.75rem',
              whiteSpace: 'normal',
            }}
          >
            <div style={{fontWeight: 600, fontSize: '0.875rem'}}>{option.label}</div>
          </button>
        ))}
        {options.length === 0 ? (
          <div style={{fontSize: '0.875rem', color: mutedColor, padding: '0.5rem'}}>
            {intl.formatMessage({id: 'CHATBOT.ATTENDANCE.NO_OPTIONS'})}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default AttendanceSchoolMultiSelect
